import React, { useState, useRef, useEffect } from "react";
import {
    Box, Typography, Grid, Card, CardContent, Button,
    Divider, LinearProgress, Skeleton,
} from "@mui/material";
import {
    DirectionsWalk, FitnessCenter, Bedtime, Favorite,
    CalendarToday, BoltOutlined, ArrowUpward, ArrowDownward,
} from "@mui/icons-material";
import AxiosInstance from "../../Common/Api/ApiHelper";
import { apiList } from "../../Common/Api/ApiList";
import { toast } from "react-toastify";
import {useNavigate, useParams} from "react-router-dom";
import {PathList} from "../../Common/Routes/Path";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";

// ─────────────────────────────────────────────────────────────
// STATIC CONFIG
// ─────────────────────────────────────────────────────────────
const scoreBands = [
    { label: "Beginner", range: "0-39",   color: "#9e9e9e" },
    { label: "Active",   range: "40-59",  color: "#2196f3" },
    { label: "Advanced", range: "60-74",  color: "#9c27b0" },
    { label: "Athlete",  range: "75-89",  color: "#ff5722" },
    { label: "Elite",    range: "90-100", color: "#ffc107" },
];

// All wearables shown in the card (order controls display order)
const ALL_WEARABLES = ["android_health", "strava", "garmin", "fitbit", "polar"];

// Maps DB provider key → display label
const WEARABLE_DISPLAY = {
    android_health: "Android Health",
    strava:       "Strava",
    garmin:       "Garmin",
    fitbit:       "Fitbit",
    polar:        "Polar",
};

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

/**
 * API change shape: { value: number, direction: 'up' | 'down' | 'neutral' }
 * Returns props ready for <MetricCard />
 */
const getChangeProps = (changeObj) => {
    if (!changeObj) return { changeText: "—", changeType: "success" };
    const { value, direction } = changeObj;
    return {
        changeText: value ?? "—",
        changeType: direction === "down" ? "error" : "success",
    };
};

/**
 * Converts weekly days map → SVGAreaChart array
 * API: { Mon: 72, Tue: 78, Wed: 0, ... }
 * Output: [{ date: "Mon", score: 72 }, ...]
 */
const weeklyToChartData = (weeklyDays) => {
    if (!weeklyDays) return [];
    const ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return ORDER.map((day) => ({ date: day, score: weeklyDays[day] || 0 }));
};

// ─────────────────────────────────────────────────────────────
// SCORE RING
// ─────────────────────────────────────────────────────────────
const ScoreRing = ({ score, level }) => {
    const radius = 80, stroke = 12;
    const nr = radius - stroke / 2;
    const circ = 2 * Math.PI * nr;
    const prog = (Math.min(score || 0, 100) / 100) * circ;

    return (
        <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <svg height={radius * 2} width={radius * 2} style={{ transform: "rotate(-90deg)" }}>
                <circle stroke="#e0e0e0" fill="transparent" strokeWidth={stroke} r={nr} cx={radius} cy={radius} />
                <circle stroke="#1a1a1a" fill="transparent" strokeWidth={stroke}
                        strokeDasharray={`${prog} ${circ}`} strokeLinecap="round"
                        r={nr} cx={radius} cy={radius} />
            </svg>
            <Box sx={{ position: "absolute", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography sx={{ fontSize: 36, fontWeight: 700, lineHeight: 1, color: "#1a1a1a" }}>
                    {score ?? "—"}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#9c27b0", fontWeight: 600, mt: 0.5 }}>
                    {level || "—"}
                </Typography>
            </Box>
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────
// METRIC CARD
// ─────────────────────────────────────────────────────────────
const MetricCard = ({ icon, label, value, unit, changeText, changeType }) => (
    <Card elevation={0} sx={{ border: "1px solid #f0f0f0", borderRadius: 3, height: "100%" }}>
        <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ color: "#666", display: "flex", alignItems: "center" }}>{icon}</Box>
                <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>{label}</Typography>
            </Box>
            <Typography sx={{ fontSize: 32, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>
                {value ?? "—"}
                <Typography component="span" sx={{ fontSize: 16, fontWeight: 500, color: "#555", ml: 0.5 }}>
                    {unit}
                </Typography>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                {changeType === "error"
                    ? <ArrowDownward sx={{ fontSize: 14, color: "#f44336" }} />
                    : <ArrowUpward sx={{ fontSize: 14, color: "#4caf50" }} />
                }
                <Typography sx={{ fontSize: 13, color: changeType === "error" ? "#f44336" : "#4caf50", fontWeight: 500 }}>
                    {changeText}
                </Typography>
            </Box>
        </CardContent>
    </Card>
);

const MetricCardSkeleton = () => (
    <Card elevation={0} sx={{ border: "1px solid #f0f0f0", borderRadius: 3, height: "100%" }}>
        <CardContent sx={{ p: 3 }}>
            <Skeleton width="55%" height={18} sx={{ mb: 1 }} />
            <Skeleton width="75%" height={40} />
            <Skeleton width="40%" height={16} sx={{ mt: 0.5 }} />
        </CardContent>
    </Card>
);

// ─────────────────────────────────────────────────────────────
// SCORE COMPONENT CARD
// ─────────────────────────────────────────────────────────────
const ScoreComponentCard = ({ icon, label, score, iconColor }) => (
    <Card elevation={0} sx={{ border: "1px solid #f0f0f0", borderRadius: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ color: iconColor, display: "flex" }}>{icon}</Box>
                    <Typography sx={{ fontWeight: 500, color: "#333" }}>{label}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#1a1a1a" }}>{score ?? "—"}</Typography>
            </Box>
            <LinearProgress variant="determinate" value={Math.min(score || 0, 100)}
                            sx={{ mt: 1.5, height: 4, borderRadius: 2, backgroundColor: "#f0f0f0",
                                "& .MuiLinearProgress-bar": { backgroundColor: iconColor, borderRadius: 2 } }} />
        </CardContent>
    </Card>
);

// ─────────────────────────────────────────────────────────────
// SVG AREA CHART (no external library)
// ─────────────────────────────────────────────────────────────
const SVGAreaChart = ({ data }) => {
    const [tooltip, setTooltip] = useState(null);
    const svgRef = useRef(null);

    if (!data || data.length < 2) {
        return (
            <Box sx={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ color: "#bbb", fontSize: 13 }}>No trend data available</Typography>
            </Box>
        );
    }

    const W = 560, H = 260, padL = 36, padR = 12, padT = 10, padB = 30;
    const chartW = W - padL - padR, chartH = H - padT - padB;
    const xStep = chartW / (data.length - 1);

    const toX = (i) => padL + i * xStep;
    const toY = (v) => padT + chartH - ((v || 0) / 100) * chartH;

    const linePath = data
        .map((d, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(d.score).toFixed(1)}`)
        .join(" ");
    const areaPath =
        linePath
        + ` L${toX(data.length - 1).toFixed(1)},${(padT + chartH).toFixed(1)}`
        + ` L${toX(0).toFixed(1)},${(padT + chartH).toFixed(1)} Z`;

    const yTicks = [0, 25, 50, 75, 100];

    const handleMouseMove = (e) => {
        if (!svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const mx = ((e.clientX - rect.left) * W) / rect.width - padL;
        const idx = Math.max(0, Math.min(data.length - 1, Math.round(mx / xStep)));
        setTooltip({ x: toX(idx), y: toY(data[idx].score), d: data[idx] });
    };

    return (
        <Box sx={{ position: "relative", width: "100%" }}>
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
                 style={{ width: "100%", height: "auto", display: "block" }}
                 onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>
                <defs>
                    <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.01" />
                    </linearGradient>
                </defs>

                {/* Y grid + labels */}
                {yTicks.map((t) => (
                    <g key={t}>
                        <line x1={padL} y1={toY(t)} x2={W - padR} y2={toY(t)} stroke="#f0f0f0" strokeWidth="1" />
                        <text x={padL - 6} y={toY(t) + 4} textAnchor="end" fontSize="10" fill="#bbb">{t}</text>
                    </g>
                ))}

                {/* Area + Line */}
                <path d={areaPath} fill="url(#areaGrad)" />
                <path d={linePath} fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

                {/* X axis labels */}
                {data.map((d, i) => (
                    <text key={i} x={toX(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="#bbb">{d.date}</text>
                ))}

                {/* Hover indicator */}
                {tooltip && (
                    <>
                        <line x1={tooltip.x} y1={padT} x2={tooltip.x} y2={padT + chartH}
                              stroke="#e0e0e0" strokeWidth="1" strokeDasharray="3 3" />
                        <circle cx={tooltip.x} cy={tooltip.y} r="5" fill="#7c3aed" stroke="#fff" strokeWidth="2" />
                    </>
                )}
            </svg>

            {tooltip && (
                <Box sx={{ position: "absolute", top: `${(tooltip.y / H) * 100}%`, left: `${(tooltip.x / W) * 100}%`,
                    transform: "translate(-50%, -130%)", backgroundColor: "#fff", border: "1px solid #e0e0e0",
                    borderRadius: 1.5, px: 1.5, py: 0.8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    pointerEvents: "none", whiteSpace: "nowrap" }}>
                    <Typography sx={{ fontSize: 11, color: "#666" }}>{tooltip.d.date}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#7c3aed" }}>Score: {tooltip.d.score}</Typography>
                </Box>
            )}
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
export default function UserFitnessDashboardPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading]                   = useState(true);

    // res.data.r.daily.its_score
    const [itsScore, setItsScore]                 = useState(null);
    // res.data.r.daily.today_activity
    const [todayActivity, setTodayActivity]       = useState(null);
    // res.data.r.weekly → converted to chart array
    const [chartData, setChartData]               = useState([]);
    const [weeklyAvg, setWeeklyAvg]               = useState(null);
    // res.data.r.connections → ["apple_health", "garmin", ...]
    const [connectedProviders, setConnectedProviders] = useState([]);

    // ── API Call ──
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await AxiosInstance.get(`${apiList.getFitnessDashboard}?id=${id}`);
            if (res.data.s === 1) {
                const { daily, weekly, connections } = res.data.r;

                // daily.its_score  → { score, level, trend, components: { activity, recovery, consistency, intensity, engagement } }
                setItsScore(daily?.its_score || null);

                // daily.today_activity → { steps, workout, sleep, resting_hr, zone_minutes, active_streak }
                // Each: { value, unit?, change: { value, direction } }  (active_streak has is_record bool)
                setTodayActivity(daily?.today_activity || null);

                // weekly.days  → { Mon: 72, Tue: 78, ... }
                // weekly.weekly_average → number
                setChartData(weeklyToChartData(weekly?.days));
                setWeeklyAvg(weekly?.weekly_average ?? null);

                // connections → [{ provider: "apple_health" }, { provider: "garmin" }]
                setConnectedProviders((connections || []).map((c) => c.provider));
            } else {
                toast.error(res.data.m || "Failed to load dashboard.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [id]);

    // ── Derived ──
    const scoreTrendText = itsScore?.trend ? `${itsScore.trend} from yesterday` : null;
    const scoreTrendUp   = itsScore?.trend ? !String(itsScore.trend).startsWith("-") : true;
    const components     = itsScore?.components;

    const steps        = todayActivity?.steps;
    const workout      = todayActivity?.workout;
    const sleep        = todayActivity?.sleep;
    const restingHR    = todayActivity?.resting_hr;
    const zoneMinutes  = todayActivity?.zone_minutes;
    const activeStreak = todayActivity?.active_streak;

    // ─────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────
    return (
        <Box>
            <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>

                {/* ── Header ── */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5, // adjust spacing as needed
                            pb: 2,
                        }}
                    >
                        <ArrowBackIosRoundedIcon
                            sx={{
                                cursor: "pointer",
                                "&:hover": {
                                    color: "#3C75D4",
                                },
                            }}
                            onClick={() => navigate(PathList.UserManagement)}
                        />

                        <Typography sx={{ fontWeight: 800, fontSize: { xs: 22, md: 26 }, color: "#1a1a1a" }}>
                            Your Fitness Intelligence Dashboard
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: 12, color: "#888", mb: 0.5 }}>Current Score</Typography>
                        {loading ? (
                            <Skeleton width={80} height={44} />
                        ) : (
                            <>
                                <Typography sx={{ fontSize: 36, fontWeight: 800, color: "#7c3aed", lineHeight: 1 }}>
                                    {itsScore?.score ?? "—"}
                                </Typography>
                                {scoreTrendText && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, justifyContent: "flex-end" }}>
                                        {scoreTrendUp
                                            ? <ArrowUpward sx={{ fontSize: 13, color: "#4caf50" }} />
                                            : <ArrowDownward sx={{ fontSize: 13, color: "#f44336" }} />
                                        }
                                        <Typography sx={{ fontSize: 12, color: scoreTrendUp ? "#4caf50" : "#f44336", fontWeight: 500 }}>
                                            {scoreTrendText}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Box>

                {/* ── Score Ring + 6 Metrics ── */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                        <Card elevation={0} sx={{ border: "1px solid #f0f0f0", borderRadius: 3, height: "100%" }}>
                            <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 2.5, color: "#1a1a1a" }}>
                                    InstaTrainMe Score
                                </Typography>
                                {loading
                                    ? <Skeleton variant="circular" width={160} height={160} />
                                    : <ScoreRing score={itsScore?.score ?? 0} level={itsScore?.level} />
                                }
                                <Typography sx={{ fontSize: 12, color: "#888", textAlign: "center", mt: 2.5, lineHeight: 1.6, maxWidth: 200 }}>
                                    Your daily fitness intelligence score based on activity, recovery, consistency, intensity, and engagement.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={9}>
                        <Grid container spacing={2}>
                            {loading ? (
                                Array.from({ length: 6 }).map((_, i) => (
                                    <Grid item xs={6} md={4} key={i}><MetricCardSkeleton /></Grid>
                                ))
                            ) : (
                                <>
                                    {/* steps.value, steps.change: { value, direction } */}
                                    <Grid item xs={6} md={4}>
                                        <MetricCard
                                            icon={<DirectionsWalk sx={{ fontSize: 18 }} />}
                                            label="Steps"
                                            value={steps?.value?.toLocaleString()}
                                            unit=""
                                            {...getChangeProps(steps?.change)}
                                        />
                                    </Grid>

                                    {/* workout.value, workout.unit = "min", workout.change */}
                                    <Grid item xs={6} md={4}>
                                        <MetricCard
                                            icon={<FitnessCenter sx={{ fontSize: 18 }} />}
                                            label="Workout"
                                            value={workout?.value}
                                            unit={workout?.unit || "min"}
                                            {...getChangeProps(workout?.change)}
                                        />
                                    </Grid>

                                    {/* sleep.value, sleep.unit = "hrs", sleep.change */}
                                    <Grid item xs={6} md={4}>
                                        <MetricCard
                                            icon={<Bedtime sx={{ fontSize: 18 }} />}
                                            label="Sleep"
                                            value={sleep?.value}
                                            unit={sleep?.unit || "hrs"}
                                            {...getChangeProps(sleep?.change)}
                                        />
                                    </Grid>

                                    {/* resting_hr.value, resting_hr.unit = "bpm", resting_hr.change */}
                                    <Grid item xs={6} md={4}>
                                        <MetricCard
                                            icon={<Favorite sx={{ fontSize: 18 }} />}
                                            label="Resting HR"
                                            value={restingHR?.value}
                                            unit={restingHR?.unit || "bpm"}
                                            {...getChangeProps(restingHR?.change)}
                                        />
                                    </Grid>

                                    {/* active_streak.value, active_streak.unit = "days", active_streak.is_record */}
                                    <Grid item xs={6} md={4}>
                                        <MetricCard
                                            icon={<CalendarToday sx={{ fontSize: 18 }} />}
                                            label="Active Streak"
                                            value={activeStreak?.value}
                                            unit={activeStreak?.unit || "days"}
                                            changeText={activeStreak?.is_record ? "New record!" : `${activeStreak?.value ?? 0} days`}
                                            changeType="success"
                                        />
                                    </Grid>

                                    {/* zone_minutes.value, zone_minutes.unit = "min", zone_minutes.change */}
                                    <Grid item xs={6} md={4}>
                                        <MetricCard
                                            icon={<BoltOutlined sx={{ fontSize: 18 }} />}
                                            label="Zone Minutes"
                                            value={zoneMinutes?.value}
                                            unit={zoneMinutes?.unit || "min"}
                                            {...getChangeProps(zoneMinutes?.change)}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                {/* ── Score Components ── */}
                <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 2, color: "#1a1a1a" }}>
                    Score Components
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {loading ? (
                        Array.from({ length: 3 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Card elevation={0} sx={{ border: "1px solid #f0f0f0", borderRadius: 3 }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Skeleton width="60%" height={24} />
                                        <Skeleton width="100%" height={8} sx={{ mt: 1.5, borderRadius: 1 }} />
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))
                    ) : (
                        <>
                            {/* components.activity  → its_score.components.activity */}
                            <Grid item xs={12} sm={6} md={4}>
                                <ScoreComponentCard
                                    icon={<BoltOutlined sx={{ fontSize: 20 }} />}
                                    label="Activity Score"
                                    score={components?.activity ?? 0}
                                    iconColor="#4caf50"
                                />
                            </Grid>

                            {/* components.recovery  → its_score.components.recovery */}
                            <Grid item xs={12} sm={6} md={4}>
                                <ScoreComponentCard
                                    icon={<Favorite sx={{ fontSize: 20 }} />}
                                    label="Recovery Score"
                                    score={components?.recovery ?? 0}
                                    iconColor="#4caf50"
                                />
                            </Grid>

                            {/* components.intensity → its_score.components.intensity */}
                            <Grid item xs={12} sm={6} md={4}>
                                <ScoreComponentCard
                                    icon={<FitnessCenter sx={{ fontSize: 20 }} />}
                                    label="Intensity Score"
                                    score={components?.intensity ?? 0}
                                    iconColor="#ff5722"
                                />
                            </Grid>
                        </>
                    )}
                </Grid>

                {/* ── Weekly Trend + Wearables ── */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={7}>
                        <Card elevation={0} sx={{ border: "1px solid #f0f0f0", borderRadius: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a" }}>
                                        Weekly Score Trend
                                    </Typography>
                                    {/* weekly.weekly_average */}
                                    {weeklyAvg !== null && !loading && (
                                        <Typography sx={{ fontSize: 12, color: "#888" }}>
                                            Avg: <strong style={{ color: "#7c3aed" }}>{weeklyAvg}</strong>
                                        </Typography>
                                    )}
                                </Box>
                                {loading
                                    ? <Skeleton variant="rectangular" width="100%" height={260} sx={{ borderRadius: 2 }} />
                                    : <SVGAreaChart data={chartData} />
                                }
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Wearables: connections array from API */}
                    <Grid item xs={12} md={5}>
                        <Card elevation={0} sx={{ border: "1px solid #f0f0f0", borderRadius: 3, height: "100%" }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 2, color: "#1a1a1a" }}>
                                    Wearable Connections
                                </Typography>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <Box key={i} sx={{ py: 1.5 }}>
                                            <Skeleton width="65%" height={20} />
                                            <Skeleton width="40%" height={14} sx={{ mt: 0.5 }} />
                                        </Box>
                                    ))
                                ) : (
                                    ALL_WEARABLES.map((providerKey, idx) => {
                                        // connections: [{ provider: "apple_health" }, ...]
                                        const isConnected = connectedProviders.includes(providerKey);
                                        const displayName = WEARABLE_DISPLAY[providerKey] || providerKey;
                                        return (
                                            <Box key={providerKey}>
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <Favorite sx={{ fontSize: 18, color: "#888" }} />
                                                        <Box>
                                                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                                                                {displayName}
                                                            </Typography>
                                                            {isConnected && (
                                                                <Typography sx={{ fontSize: 11, color: "#888" }}>
                                                                    Connected via app
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        {isConnected && (
                                                            <Typography sx={{ fontSize: 12, color: "#4caf50", fontWeight: 600 }}>
                                                                Connected
                                                            </Typography>
                                                        )}
                                                        <Button variant="outlined" size="small" type="button"
                                                                sx={{ textTransform: "none", fontSize: 12, fontWeight: 500,
                                                                    borderRadius: 1.5, px: 1.5, py: 0.4, borderColor: "#ddd",
                                                                    color: "#333", "&:hover": { borderColor: "#bbb", backgroundColor: "#f9f9f9" } }}>
                                                            {isConnected ? "Disconnect" : "Connect"}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                                {idx < ALL_WEARABLES.length - 1 && <Divider sx={{ borderColor: "#f5f5f5" }} />}
                                            </Box>
                                        );
                                    })
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ── Score Bands ── */}
                <Card elevation={0} sx={{ border: "1px solid #f0f0f0", borderRadius: 3, mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 2.5, color: "#1a1a1a" }}>
                            Score Bands
                        </Typography>
                        <Box sx={{ display: "flex", mb: 1.5, borderRadius: 2, overflow: "hidden" }}>
                            {scoreBands.map((band) => (
                                <Box key={band.label} sx={{ flex: 1, height: 10, backgroundColor: band.color }} />
                            ))}
                        </Box>
                        <Grid container>
                            {scoreBands.map((band) => (
                                <Grid item xs key={band.label} sx={{ textAlign: "center" }}>
                                    <Typography sx={{ fontSize: { xs: 10, sm: 12 }, fontWeight: 700, color: "#333" }}>
                                        {band.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: { xs: 10, sm: 12 }, color: "#888" }}>
                                        {band.range}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}